import {
  Flex,
  FormChipMultipleSelection,
  FormDateInput,
  FormTimeInput,
  Grid,
  Text,
} from '@shared/components';
import { dayjs } from '@shared/utils';

export interface RecurringExamScheduleFormFieldProps {
  slotProps: {
    examDateGroup: {
      examRecurringEndDate?: Date | null;
      startTime?: Date | null;
      endTime?: Date | null;
      minDate: Date;
      maxDate: Date;
    };
    eventEndDate: {
      examRecurringStartDate?: Date | null;
    };
  };
}

const DAYS_OF_WEEK_OPTIONS = [
  { label: 'จันทร์', value: 'MON' },
  { label: 'อังคาร', value: 'TUE' },
  { label: 'พุธ', value: 'WED' },
  { label: 'พฤหัสบดี', value: 'THU' },
  { label: 'ศุกร์', value: 'FRI' },
  { label: 'เสาร์', value: 'SAT' },
  { label: 'อาทิตย์', value: 'SUN' },
];

export default function RecurringExamScheduleFormField(
  props: Readonly<RecurringExamScheduleFormFieldProps>,
) {
  const { slotProps } = props;
  const { examDateGroup, eventEndDate } = slotProps ?? {};
  const minExamRecurringStartDate = dayjs
    .max(dayjs().add(1, 'day'), dayjs(slotProps?.examDateGroup?.minDate).startOf('month'))
    .toDate();
  const maxExamRecurringStartDate = dayjs(slotProps?.examDateGroup?.maxDate).toDate();

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      <Grid
        sx={(theme) => ({
          columnGap: theme.spacing(6),
          rowGap: theme.spacing(6),
          gridTemplateColumns: '240px 1fr',
        })}
      >
        <Text variant='body1Semibold'>ช่วงวันที่สอบ</Text>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <Grid
            sx={{
              columnGap: (theme) => theme.spacing(2),
              rowGap: (theme) => theme.spacing(2),
              gridTemplateColumns:
                'minmax(max-content, 200px) max-content minmax(max-content, 200px)',
            }}
          >
            <FormDateInput
              name='examDateGroup.examRecurringStartDate'
              label='วันที่เริ่มช่วงสอบ'
              inputFormat='DD-MM-YYYY'
              placeholder='DD-MM-YYYY'
              minDate={minExamRecurringStartDate}
              maxDate={examDateGroup?.examRecurringEndDate ?? maxExamRecurringStartDate}
              initialMonth={slotProps?.examDateGroup?.minDate}
              defaultTime={{
                hour: 0,
                minute: 0,
                second: 0,
              }}
              rules={{
                required: true,
              }}
            />
            <Text sx={{ p: (theme) => theme.spacing(8.5, 1, 2.5, 1) }}>ถึง</Text>
            <FormDateInput
              name='examDateGroup.examRecurringEndDate'
              label='วันที่สิ้นสุดช่วงสอบ'
              inputFormat='DD-MM-YYYY'
              placeholder='DD-MM-YYYY'
              initialMonth={slotProps?.examDateGroup?.minDate}
              minDate={eventEndDate.examRecurringStartDate ?? minExamRecurringStartDate}
              maxDate={maxExamRecurringStartDate}
              rules={{
                required: true,
              }}
            />
          </Grid>
          <FormChipMultipleSelection
            name='examDateGroup.date'
            options={DAYS_OF_WEEK_OPTIONS}
            label='เลือกวันในสัปดาห์'
            row
            sx={(theme) => ({
              color: theme.palette.grey[500],
              alignItems: 'flex-start',
              '& .MuiChip-root': {
                border: 'none',
                backgroundColor: theme.palette.grey[100],
                width: '85px',
                height: '32px',
                p: theme.spacing(1.5, 4),
                '&.MuiChip-clickable:hover': {
                  backgroundColor: theme.palette.grey[100],
                  '&.MuiChip-clickableColorPrimary': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                  },
                },
                '&.MuiChip-clickableColorPrimary': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.text.primary,
                },
              },
            })}
            required
          />
        </Flex>
      </Grid>
      <Grid
        sx={(theme) => ({
          columnGap: theme.spacing(6),
          rowGap: theme.spacing(6),
          gridTemplateColumns: '240px 1fr',
        })}
      >
        <Text variant='body1Semibold'>เวลาสอบ</Text>
        <Grid
          sx={{
            columnGap: (theme) => theme.spacing(2),
            rowGap: (theme) => theme.spacing(2),
            gridTemplateColumns:
              'minmax(max-content, 200px) max-content minmax(max-content, 200px)',
          }}
        >
          <FormTimeInput
            name='examDateGroup.startTime'
            label='เวลาเริ่มต้น'
            rules={{
              required: true,
            }}
          />
          <Text sx={{ p: (theme) => theme.spacing(8.5, 1, 2.5, 1) }}>ถึง</Text>
          <FormTimeInput
            name='examDateGroup.endTime'
            label='เวลาสิ้นสุด'
            rules={{
              required: true,
            }}
          />
        </Grid>
      </Grid>
    </Flex>
  );
}
